import type { ComputedRef, Ref } from "vue";
import { computed, watchEffect } from "vue";

import { linkDragSource, useLinkDropTarget } from "@/composables/linkDrag";
import type { Card } from "@/model/card";
import { useBoardStore } from "@/store/board";
import { linkBetween } from "@/store/helpers/link";
import { injectStrict } from "@/utils/vue";

import { boardKey } from "../../board/injectKeys";

interface Props {
  el: Ref<HTMLElement | null>;
  card: Card;
  disabled?: Ref<Readonly<boolean>>;
}

interface UseLinkDragReturn {
  isLinkingTarget: ComputedRef<Readonly<boolean>>;
  isLinkingSource: ComputedRef<Readonly<boolean>>;
}

// Sticky-as-drop-target. The source-side handle lives in StickyNoteLinks.vue
// via <LinkDragHandle>.
export function useLinkDrag({ el, card, disabled }: Props): UseLinkDragReturn {
  const board = injectStrict(boardKey);

  const { isDropTarget, onPointerEnter, onPointerLeave } = useLinkDropTarget(
    () => ({ kind: "sticky", boardId: board.value.id, id: card.id }),
    {
      // Reject duplicate sticky↔sticky links (DB constraint).
      isValid: (source) => {
        if (source.kind !== "sticky") return true;
        const sourceCard = useBoardStore().currentBoard().cards[source.id]?.data;
        return !sourceCard || !linkBetween(card, sourceCard);
      },
    },
  );

  // Re-syncs listeners when `disabled` flips (e.g. another user starts/stops
  // editing the sticky) and cleans up on unmount.
  watchEffect(
    (onCleanup) => {
      const target = el.value;
      if (!target || disabled?.value) return;
      target.addEventListener("pointerenter", onPointerEnter);
      target.addEventListener("pointerleave", onPointerLeave);
      onCleanup(() => {
        target.removeEventListener("pointerenter", onPointerEnter);
        target.removeEventListener("pointerleave", onPointerLeave);
      });
    },
    { flush: "post" },
  );

  const isLinkingTarget = isDropTarget;
  const isLinkingSource = computed(
    () => linkDragSource.value?.kind === "sticky" && linkDragSource.value.id === card.id,
  );

  return { isLinkingTarget, isLinkingSource };
}
